/* Real HTTP API implementation of RangeClient */
import { IRangeClient } from './IRangeClient';
import {
  RangeDto,
  RangeStatsDto,
  MenuDto,
  MenuStatsDto,
  RangeMenuDto,
  MenuRangeDto,
  MenuRecipeDto,
  RecipeCandidateDto,
  ServicePeriodDto,
  CreateRangeDto,
  UpdateRangeDto,
  CreateMenuDto,
  UpdateMenuDto,
  PagedData,
  ListRangesParams,
  ListMenusParams,
  ListAvailableRecipesParams,
} from './rangeTypes';

export class RangeClientApi implements IRangeClient {
  private baseUrl: string;

  constructor(baseUrl: string = '/api/range') {
    this.baseUrl = baseUrl;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });

    let store: { storeNumber?: string; franchiseId?: number } | null = null;
    const storedStore = localStorage.getItem('selectedStore');
    if (storedStore) {
      try {
        store = JSON.parse(storedStore);
      } catch {
        store = null;
      }
    }

    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(store && {
        'X-Context-Store-Number': store.storeNumber || '',
        'X-Context-Franchise-Id': store.franchiseId ? String(store.franchiseId) : '',
      }),
    };
  }

  // Extracts a human-readable message from ASP.NET Core error responses.
  // Handles both custom { message } payloads and RFC 7807 Problem Details { detail, title }.
  private async extractErrorMessage(response: Response, fallback: string): Promise<string> {
    try {
      const errorData = await response.json();
      return errorData.message || errorData.detail || errorData.title || fallback;
    } catch {
      return fallback;
    }
  }

  // ==================== Ranges ====================

  async getRanges(params: ListRangesParams): Promise<PagedData<RangeDto>> {
    const headers = await this.getHeaders();
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);

    const response = await fetch(`${this.baseUrl}/ranges?${query.toString()}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch ranges');
    const result = await response.json();
    return result.data;
  }

  async getRangeStats(): Promise<RangeStatsDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/ranges/stats`, { headers });
    if (!response.ok) throw new Error('Failed to fetch range statistics');
    const result = await response.json();
    return result.data;
  }

  async getRange(id: number): Promise<RangeDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/ranges/${id}`, { headers });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch range');
    const result = await response.json();
    return result.data;
  }

  async createRange(dto: CreateRangeDto): Promise<RangeDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/ranges`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to create range'));
    }
    const result = await response.json();
    return result.data;
  }

  async updateRange(id: number, dto: UpdateRangeDto): Promise<RangeDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/ranges/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to update range'));
    }
    const result = await response.json();
    return result.data;
  }

  async deleteRange(id: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/ranges/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to delete range'));
    }
  }

  // ==================== Range <-> Menu membership ====================

  async getRangeMenus(rangeId: number): Promise<RangeMenuDto[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/ranges/${rangeId}/menus`, { headers });
    if (!response.ok) throw new Error('Failed to fetch range menus');
    const result = await response.json();
    return result.data;
  }

  // ==================== Menus ====================

  async getMenus(params: ListMenusParams): Promise<PagedData<MenuDto>> {
    const headers = await this.getHeaders();
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.menuType) query.set('menuType', params.menuType);
    if (params.displayStatus) query.set('displayStatus', params.displayStatus);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);

    const response = await fetch(`${this.baseUrl}/menus?${query.toString()}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch menus');
    const result = await response.json();
    return result.data;
  }

  async getMenuStats(): Promise<MenuStatsDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/menus/stats`, { headers });
    if (!response.ok) throw new Error('Failed to fetch menu statistics');
    const result = await response.json();
    return result.data;
  }

  async getMenu(id: number): Promise<MenuDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/menus/${id}`, { headers });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch menu');
    const result = await response.json();
    return result.data;
  }

  async createMenu(dto: CreateMenuDto): Promise<MenuDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/menus`, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to create menu'));
    }
    const result = await response.json();
    return result.data;
  }

  async updateMenu(id: number, dto: UpdateMenuDto): Promise<MenuDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/menus/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to update menu'));
    }
    const result = await response.json();
    return result.data;
  }

  async deleteMenu(id: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/menus/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to delete menu'));
    }
  }

  // ==================== Menu <-> Recipe ====================

  async getMenuRecipes(menuId: number): Promise<MenuRecipeDto[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/menus/${menuId}/recipes`, { headers });
    if (!response.ok) throw new Error('Failed to fetch menu recipes');
    const result = await response.json();
    return result.data;
  }

  async getAvailableRecipes(params: ListAvailableRecipesParams): Promise<PagedData<RecipeCandidateDto>> {
    const headers = await this.getHeaders();
    const query = new URLSearchParams();
    query.set('franchiseId', String(params.franchiseId));
    if (params.search) query.set('search', params.search);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));

    const response = await fetch(`${this.baseUrl}/menus/available-recipes?${query.toString()}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch available recipes');
    const result = await response.json();
    return result.data;
  }

  // ==================== Menu Service Periods ====================

  async getServicePeriods(menuId: number): Promise<ServicePeriodDto[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/menus/${menuId}/service-periods`, { headers });
    if (!response.ok) throw new Error('Failed to fetch service periods');
    const result = await response.json();
    return result.data;
  }

  async getMenuRanges(menuId: number): Promise<MenuRangeDto[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/menus/${menuId}/ranges`, { headers });
    if (!response.ok) throw new Error('Failed to fetch menu ranges');
    const result = await response.json();
    return result.data;
  }
}
